import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI } from '../services/api';
import { Box, Typography, CircularProgress, Alert, Breadcrumbs, Link } from '@mui/material';
import { firmService } from '../services/firmService';
import ProjectForm, { ProjectFormValues } from '../components/ProjectForm';
import { DEFAULT_CASE_STATUS_NOTIFICATION_STATUSES } from '../constants/caseStatusNotifications';

const ProjectEdit: React.FC = () => {
  const { projectId, firmId } = useParams<{ projectId: string; firmId?: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState<any>({ id: '', firm_id: '', name: '', description: '', status: 'active' });
  const [form, setForm] = useState<ProjectFormValues>({
    name: '',
    description: '',
    enable_checklist: false,
    checklist_instructions: '',
    billing_type: '',
    rate: '',
    flat_rate: '',
    flat_rate_page_from: '',
    flat_rate_page_to: '',
    variable_rate: '',
    variable_rate_page_from: '',
    variable_rate_page_to: '',
    additional_claimant: '',
    appendix_c_rate: '',
    appendix_c_rate_ranges: [],
    free_cases_allowed: '0',
    hide_case_timer: false,
    enable_estimate_approval: false,
    max_estimate_hours: '',
    enable_sample_estimate: false,
    max_capacity_pages_per_hour: '0',
    remind_on_file_missing: false,
    due_date_required: true,
    notify_admins_on_case_status_change: false,
    case_status_change_notification_statuses: [...DEFAULT_CASE_STATUS_NOTIFICATION_STATUSES],
    enable_disposition: false,
    mandatory_disposition_field: false,
    disposition_list: [],
    status: 'active',
  });
  const [firmName, setFirmName] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await projectAPI.getById(Number(projectId));
        const proj = res.data.data;
        setProject(proj);
        const custom = proj.settings?.custom_fields || {};
        setForm({
          name: proj.name || '',
          description: proj.description || '',
          enable_checklist: Boolean(custom.enable_checklist),
          checklist_instructions: custom.checklist_instructions || '',
          billing_type: (custom.billing_type as any) || '',
          rate: custom.rate !== undefined && custom.rate !== null ? String(custom.rate) : '',
          flat_rate: custom.flat_rate !== undefined && custom.flat_rate !== null ? String(custom.flat_rate) : '',
          flat_rate_page_from: custom.flat_rate_page_from !== undefined && custom.flat_rate_page_from !== null ? String(custom.flat_rate_page_from) : '',
          flat_rate_page_to: custom.flat_rate_page_to !== undefined && custom.flat_rate_page_to !== null ? String(custom.flat_rate_page_to) : '',
          variable_rate: custom.variable_rate !== undefined && custom.variable_rate !== null ? String(custom.variable_rate) : '',
          variable_rate_page_from: custom.variable_rate_page_from !== undefined && custom.variable_rate_page_from !== null ? String(custom.variable_rate_page_from) : '',
          variable_rate_page_to: custom.variable_rate_page_to !== undefined && custom.variable_rate_page_to !== null ? String(custom.variable_rate_page_to) : '',
          additional_claimant: custom.additional_claimant !== undefined && custom.additional_claimant !== null ? String(custom.additional_claimant) : '',
          appendix_c_rate: custom.appendix_c_rate !== undefined && custom.appendix_c_rate !== null ? String(custom.appendix_c_rate) : '',
          appendix_c_rate_ranges: Array.isArray(custom.appendix_c_rate_ranges)
            ? custom.appendix_c_rate_ranges.map((r: any) => ({
                from: r.from !== undefined && r.from !== null ? String(r.from) : '',
                to: r.to !== undefined && r.to !== null ? String(r.to) : '',
                rate: r.rate !== undefined && r.rate !== null ? String(r.rate) : '',
              }))
            : custom.appendix_c_rate
              ? [{ from: '1', to: '', rate: String(custom.appendix_c_rate) }]
              : [],
          free_cases_allowed: custom.free_cases_allowed !== undefined && custom.free_cases_allowed !== null ? String(custom.free_cases_allowed) : '0',
          hide_case_timer: Boolean(custom.hide_case_timer),
          enable_estimate_approval: Boolean(custom.enable_estimate_approval),
          max_estimate_hours: custom.max_estimate_hours !== undefined && custom.max_estimate_hours !== null ? String(custom.max_estimate_hours) : '',
          enable_sample_estimate: Boolean(custom.enable_sample_estimate),
          max_capacity_pages_per_hour: custom.max_capacity_pages_per_hour !== undefined && custom.max_capacity_pages_per_hour !== null ? String(custom.max_capacity_pages_per_hour) : '0',
          remind_on_file_missing: Boolean(custom.remind_on_file_missing),
          due_date_required: proj.settings?.notification_settings?.allow_optional_due_date !== true,
          notify_admins_on_case_status_change: Boolean(proj.settings?.notification_settings?.notify_admins_on_case_status_change),
          case_status_change_notification_statuses:
            proj.settings?.notification_settings?.case_status_change_notification_statuses
            ?? [...DEFAULT_CASE_STATUS_NOTIFICATION_STATUSES],
          enable_disposition: Boolean(custom.enable_disposition),
          mandatory_disposition_field: Boolean(custom.mandatory_disposition_field),
          disposition_list: Array.isArray(custom.disposition_list) ? custom.disposition_list : [],
          status: (proj.status as 'active' | 'draft') || 'active',
        });
      } catch (e: any) {
        setError(e.message || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  useEffect(() => {
    const loadFirm = async () => {
      const id = project.firm_id || firmId;
      if (!id) return;
      try {
        const res = await firmService.getFirmById(parseInt(String(id)));
        setFirmName(res.data.name);
      } catch {}
    };
    loadFirm();
  }, [project.firm_id, firmId]);

  const handleSubmitForm = async (values: ProjectFormValues) => {
    try {
      setSaving(true);
      await projectAPI.update(Number(projectId), { name: values.name, description: values.description, status: values.status });
      const custom_fields = {
        billing_type: values.billing_type,
        rate: values.billing_type !== 'Flat Rate' && values.rate ? Number(values.rate) : null,
        flat_rate: values.billing_type === 'Flat Rate' && values.flat_rate ? Number(values.flat_rate) : null,
        flat_rate_page_from: values.billing_type === 'Flat Rate' && values.flat_rate_page_from ? Number(values.flat_rate_page_from) : null,
        flat_rate_page_to: values.billing_type === 'Flat Rate' && values.flat_rate_page_to ? Number(values.flat_rate_page_to) : null,
        variable_rate: values.billing_type === 'Flat Rate' && values.variable_rate ? Number(values.variable_rate) : null,
        variable_rate_page_from: values.billing_type === 'Flat Rate' && values.variable_rate_page_from ? Number(values.variable_rate_page_from) : null,
        variable_rate_page_to: values.billing_type === 'Flat Rate' && values.variable_rate_page_to ? Number(values.variable_rate_page_to) : null,
        additional_claimant: values.billing_type === 'Flat Rate' && values.additional_claimant ? Number(values.additional_claimant) : null,
        appendix_c_rate: (values.billing_type === 'Consolidation' || values.billing_type === 'Page Based') && values.appendix_c_rate ? Number(values.appendix_c_rate) : null,
        appendix_c_rate_ranges: values.billing_type === 'Page Based' && values.appendix_c_rate_ranges?.length
          ? values.appendix_c_rate_ranges
              .filter((r) => r.rate)
              .map((r) => ({
                from: Number(r.from || 0),
                to: r.to ? Number(r.to) : null,
                rate: Number(r.rate),
              }))
          : null,
        free_cases_allowed: values.free_cases_allowed ? Number(values.free_cases_allowed) : 0,
        hide_case_timer: values.hide_case_timer,
        enable_checklist: values.enable_checklist,
        checklist_instructions: values.checklist_instructions,
        checklist_instructions_format: values.enable_checklist ? 'Full HTML' : undefined,
        enable_estimate_approval: values.enable_estimate_approval,
        max_estimate_hours: values.max_estimate_hours ? Number(values.max_estimate_hours) : null,
        enable_sample_estimate: values.enable_sample_estimate,
        max_capacity_pages_per_hour: values.enable_sample_estimate ? Number(values.max_capacity_pages_per_hour || '0') : null,
        remind_on_file_missing: values.remind_on_file_missing,
        enable_disposition: values.enable_disposition,
        mandatory_disposition_field: values.enable_disposition ? values.mandatory_disposition_field : false,
        disposition_list: values.enable_disposition ? values.disposition_list : [],
        body_format: 'Full HTML',
      };
      await projectAPI.updateSettings(Number(projectId), {
        custom_fields,
        notification_settings: {
          notify_admins_on_case_status_change: values.notify_admins_on_case_status_change,
          case_status_change_notification_statuses: values.case_status_change_notification_statuses,
          allow_optional_due_date: !values.due_date_required,
        },
      });

      if (project.firm_id || firmId) {
        navigate(`/firms/${project.firm_id || firmId}/projects/${projectId}`);
      } else {
        navigate(`/projects/${projectId}`);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      {(project.firm_id || firmId) && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
            sx={{ textDecoration: 'none' }}
          >
            Firms
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${project.firm_id || firmId}`); }}
            sx={{ textDecoration: 'none' }}
          >
            {firmName || `Firm #${project.firm_id || firmId}`}
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${project.firm_id || firmId}/projects/${projectId}`); }}
            sx={{ textDecoration: 'none' }}
          >
            {project.name || `Project #${projectId}`}
          </Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>
      )}
      <Typography variant="h4" gutterBottom>Edit Project</Typography>
      <ProjectForm
        initialValues={form}
        submitting={saving}
        mode="edit"
        showStatusSelect
        onSubmit={handleSubmitForm}
        onCancel={() => navigate(-1)}
        projectId={Number(projectId)}
      />
    </Box>
  );
};

export default ProjectEdit;
